// Pagina dettaglio ticket portale — tracker step verticale + ETA tecnico real-time.
// Aggiornamenti in tempo reale via Socket.io canale ticket:{id}.
import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, AlertCircle, Clock, Tag, MapPin } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { StatusPill } from '../../components/ui/StatusPill'
import { Skeleton } from '../../components/ui/Skeleton'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { InterventionTracker } from './components/InterventionTracker'
import { TechnicianETA } from './components/TechnicianETA'
import { portalApiCalls } from './portal.api'
import { formatDate } from '../../lib/utils'
import type { TicketStatus } from '@fieldcore/types'

// Importazione lazy del socket per evitare connessioni non necessarie
let socketInstance: ReturnType<typeof import('../../lib/socket')['getSocket']> | null = null

export function PortalTicketDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'ticket', id],
    queryFn: () => portalApiCalls.getTicketDetail(id!).then((r) => r.data),
    enabled: Boolean(id),
    refetchInterval: 30_000, // polling ogni 30s come fallback
  })

  // Connessione real-time Socket.io al canale ticket:{id}
  useEffect(() => {
    if (!id) return

    // Caricamento dinamico del socket per evitare connessioni in SSR
    import('../../lib/socket').then(({ getSocket }) => {
      // Usa il token portale per l'autenticazione socket
      const stored = localStorage.getItem('fc-portal-auth')
      let token: string | undefined
      if (stored) {
        try {
          const { state } = JSON.parse(stored) as { state: { accessToken: string | null } }
          token = state.accessToken ?? undefined
        } catch {
          // Ignora
        }
      }

      // Connessione con autenticazione portale
      const { io } = require('socket.io-client')
      const wsUrl = import.meta.env.VITE_WS_URL ?? 'http://localhost:3001'
      socketInstance = io(wsUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
      })

      const socket = socketInstance!

      // Join al canale del ticket specifico
      socket.emit('ticket:join', { ticketId: id })

      // Ascolta aggiornamenti di stato ticket
      socket.on('ticket:statusChanged', (payload: { ticketId: string }) => {
        if (payload.ticketId === id) {
          void queryClient.invalidateQueries({ queryKey: ['portal', 'ticket', id] })
        }
      })

      // Ascolta aggiornamento geolocalizzazione tecnico
      socket.on('geolocation:update', () => {
        void queryClient.invalidateQueries({ queryKey: ['portal', 'ticket', id] })
      })
    }).catch(() => {
      // Socket non disponibile — il polling è il fallback
    })

    return () => {
      if (socketInstance) {
        socketInstance.emit('ticket:leave', { ticketId: id })
        socketInstance.disconnect()
        socketInstance = null
      }
    }
  }, [id, queryClient])

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <AlertCircle className="mb-3 h-10 w-10 text-red-400" aria-hidden="true" />
        <h2 className="mb-2 text-lg font-semibold text-gray-900">Ticket non trovato</h2>
        <p className="mb-4 text-sm text-gray-500">
          Il ticket richiesto non esiste o non appartiene alla tua azienda.
        </p>
        <Button variant="secondary" onClick={() => navigate('/portal/tickets')}>
          Torna alla lista ticket
        </Button>
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton height={40} className="w-64" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Skeleton height={200} className="rounded-xl" />
            <Skeleton height={300} className="rounded-xl" />
          </div>
          <Skeleton height={400} className="rounded-xl" />
        </div>
      </div>
    )
  }

  const { ticket, steps, technicianEta } = data

  return (
    <div className="space-y-6">
      {/* Intestazione */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
          onClick={() => navigate('/portal/tickets')}
          aria-label="Torna alla lista ticket"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          I miei ticket
        </button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-bold text-gray-900">{ticket.title}</h1>
            <StatusPill status={ticket.status as TicketStatus} />
          </div>
          <p className="mt-1 font-mono text-sm text-gray-500">{ticket.ticketNumber}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Colonna principale */}
        <div className="space-y-6 lg:col-span-2">
          {/* Info ticket */}
          <Card>
            <CardHeader>
              <CardTitle>Dettagli richiesta</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ticket.description && (
                <div>
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                    Descrizione
                  </p>
                  <p className="text-sm text-gray-700">{ticket.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
                <div className="flex items-start gap-2">
                  <Clock className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  <div>
                    <p className="text-xs text-gray-400">Aperto il</p>
                    <p className="font-medium text-gray-900">
                      {formatDate(ticket.createdAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Tag className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  <div>
                    <p className="text-xs text-gray-400">Priorità</p>
                    <p className="font-medium capitalize text-gray-900">{ticket.priority}</p>
                  </div>
                </div>
                {ticket.scheduledStart && (
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
                    <div>
                      <p className="text-xs text-gray-400">Data pianificata</p>
                      <p className="font-medium text-gray-900">
                        {formatDate(ticket.scheduledStart, 'dd/MM/yyyy')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {ticket.slaDeadline && (
                <div className="rounded-lg bg-amber-50 px-4 py-3">
                  <p className="text-xs font-medium text-amber-700">
                    Scadenza SLA:{' '}
                    <span className="font-bold">
                      {formatDate(ticket.slaDeadline)}
                    </span>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tracker avanzamento */}
          <Card>
            <CardHeader>
              <CardTitle>Stato avanzamento</CardTitle>
            </CardHeader>
            <CardContent>
              <InterventionTracker
                steps={steps}
                currentStatus={ticket.status}
              />
            </CardContent>
          </Card>

          {/* Rating (visibile solo se completato) */}
          {ticket.status === 'completed' && (
            <Card className="border-green-200 bg-green-50">
              <CardContent>
                <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">Intervento completato!</p>
                    <p className="text-sm text-gray-600">
                      Hai ricevuto il rapportino? Lascia un feedback sulla qualità del servizio.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/portal/rating?ticketId=${ticket.id}`)}
                  >
                    Valuta intervento
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Colonna laterale — ETA tecnico */}
        <div>
          {technicianEta.available ? (
            <TechnicianETA
              technicianName={technicianEta.technicianName}
              distanceKm={technicianEta.distanceKm}
              etaMinutes={technicianEta.etaMinutes}
              lastLat={technicianEta.lastLat}
              lastLng={technicianEta.lastLng}
            />
          ) : (
            <Card className="text-center">
              <CardContent className="py-8">
                <MapPin className="mx-auto mb-3 h-10 w-10 text-gray-200" aria-hidden="true" />
                <p className="text-sm font-medium text-gray-900">Nessun tecnico in arrivo</p>
                <p className="mt-1 text-xs text-gray-400">
                  {ticket.status === 'new'
                    ? 'Il ticket è in attesa di assegnazione'
                    : ticket.status === 'assigned'
                    ? 'Il tecnico non ha ancora condiviso la posizione'
                    : ticket.status === 'completed'
                    ? 'Intervento completato'
                    : 'ETA non disponibile'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
